/**
 * Deferred module loading for things behind a user action.
 *
 * Route-level splitting moved the twenty page modules out of the entry bundle,
 * but it left the heaviest code where it was: a route still downloads everything
 * its page *might* open. The inventory list pulled the scanner (17 KB), the
 * master-data form and its wizard/icon-picker stack (37 KB) and the add-item
 * modal chain (24 KB) — none of which exists until a tap.
 *
 * Gating those naively trades one cost for another: the load gets lighter and the
 * first tap gets a network round trip. So a gate here comes in two halves, and
 * both matter —
 *
 *   load()      awaited at the call site, when the user acts
 *   prefetch()  called once the page has settled, at idle
 *
 * The tap is warm because of the prefetch; the cold start is light because of the
 * gate. Using only the first half is the version of this that makes the app feel
 * worse.
 */

import { toast } from './components/toast.js';
import { t } from './i18n.js';

/**
 * Wrap a dynamic import so it is fetched at most once and can be warmed early.
 *
 * The cached promise is dropped on rejection so a later call calls `import()`
 * again. Be aware of how little that buys: the browser memoises the failed module
 * in its own module map, so the retried import resolves to the same rejection
 * without touching the network (measured in Chromium — a retry issued zero
 * requests). A tap that failed offline therefore keeps failing, with the network
 * toast each time, until the document is reloaded.
 *
 * That is deliberate for interaction gates. The route-level equivalent reloads the
 * document to recover (see `lazy()` in router.js), which is acceptable when the
 * page never rendered — but doing it because a modal failed to open would throw
 * away the list, the scroll position and the filters the user was working with.
 * Repeating an honest error beats destroying their context.
 *
 * @template T
 * @param {() => Promise<T>} loader
 * @returns {{ load: () => Promise<T>, prefetch: () => void }}
 */
export function lazyModule(loader) {
  /** @type {Promise<T>|null} */
  let inFlight = null;

  const load = () => {
    if (!inFlight) {
      inFlight = loader().catch((err) => {
        inFlight = null;
        throw err;
      });
    }
    return inFlight;
  };

  return {
    load,
    // Never rejects — a speculative fetch that fails must not become an
    // unhandled rejection, and the real navigation will surface the problem.
    prefetch() { load().catch(() => {}); },
  };
}

/**
 * True when speculative fetching would be rude: the user asked for less data, or
 * the connection is slow enough that a prefetch would contend with the request
 * they are actually waiting for.
 *
 * @returns {boolean}
 */
function prefetchDiscouraged() {
  const conn = typeof navigator !== 'undefined' ? navigator.connection : null;
  if (!conn) return false;
  return Boolean(conn.saveData) || /(^|-)2g$/.test(conn.effectiveType || '');
}

/**
 * Run a warm-up at idle, unless the connection says not to.
 *
 * The single implementation of this policy. It was written twice before — once
 * for route chunks in main.js and once for interaction chunks — and two copies of
 * a "should we prefetch?" rule drift into two different answers.
 *
 * @param {() => void} fn
 */
export function prefetchWhenIdle(fn) {
  if (prefetchDiscouraged()) return;
  if (typeof requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 3000 });
  else setTimeout(fn, 1500);
}

/**
 * Warm several lazy modules together once the page has settled.
 *
 * @param {...{ prefetch: () => void }} mods
 */
export function prefetchAll(...mods) {
  prefetchWhenIdle(() => { for (const m of mods) m.prefetch(); });
}

/**
 * Load a gated module for a user action, reporting failure instead of throwing.
 *
 * The failure case is a tap that produces nothing — offline, and the chunk was
 * never cached. Left as a rejection it would be an unhandled promise inside an
 * event listener: no modal, no scanner, no message, and the user taps again. So
 * the caller gets `null` and the user gets the same network toast every other
 * failed request in the app shows.
 *
 * @template T
 * @param {{ load: () => Promise<T> }} mod
 * @returns {Promise<T|null>}
 */
export async function loadOrNotify(mod) {
  try {
    return await mod.load();
  } catch {
    toast.show(t('errors.network'), 'error');
    return null;
  }
}
